import { useCallback, useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { useSearchParams } from "react-router-dom";
import { Stack, Text } from "@mantine/core";
import "../sideMenu/style.scss";
import { activeTabAtom } from "../../utils/atoms/activeTabAtom";
import { projectDetailsAtom } from "../../utils/atoms/projectDetailsAtom";
import { componentStatusAtom } from "../../utils/atoms/componentStatus";
import { useTranslation, Trans } from "react-i18next";
import { useCurrentDesignComponentLists, useCurrentDesignVersion, useDesignVersionStatusAndComponents, useTabAccessWithFallback, getComponentDisplayName, isDesignVersionReadOnly } from "../../constants/helper";
import SEConfirmModal from "../common/SEConfirmModal";
import { navigateToComponentAtom } from "../../utils/atoms/navigateToComponentAtom";
import { saveTriggerAtom } from "../../utils/atoms/saveTriggerAtom";
import MGAAPI from "../../api";
import { getErrorMessage } from "../../utils/errorUtils";
import displayError from "../../constants/utils";

const MGA_API = new MGAAPI();

const ManageProgressBar = () => {
  const [activeState, setActiveState] = useAtom(activeTabAtom);
  const [searchParams, setSearchParams] = useSearchParams();
  const [projectDetails] = useAtom(projectDetailsAtom);
  const [componentStatus] = useAtom(componentStatusAtom);
  const [saveTrigger] = useAtom(saveTriggerAtom);
  const { t } = useTranslation();

  useEffect(() => {
    const handleTabChange = () => {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get("tab")?.toUpperCase();
      if (["DESIGN", "SIMULATION", "RESULT"].includes(tab)) {
        setActiveState(tab);
      }
    };

    // Run on mount
    handleTabChange();

    // Listen for popstate (back/forward navigation)
    window.addEventListener("popstate", handleTabChange);
    return () => window.removeEventListener("popstate", handleTabChange);
  }, [setActiveState]);
  const selectedVersion = useCurrentDesignVersion(projectDetails?.designWithVersionsDTOList);
  const isVersionReadOnly = isDesignVersionReadOnly(selectedVersion?.versionStatus);

//  const {
//     modal: fallbackModal
//   }   = useTabAccessWithFallback(projectDetails?.designWithVersionsDTOList, projectDetails?.designComponentsList)
  const canViewTabs = useDesignVersionStatusAndComponents(projectDetails?.designWithVersionsDTOList, projectDetails?.designComponentsList)
  const selectedDesignAndDesignVersion = useCurrentDesignComponentLists(projectDetails?.designComponentsList);
  const savedGenset = selectedDesignAndDesignVersion?.componentDTOList?.find(
    (component) => component.componentType?.toLowerCase() === "baseload genset"
  );
  const gensetDesignId = selectedDesignAndDesignVersion?.usedDesignId;
  const gensetDesignVersionId =
    selectedDesignAndDesignVersion?.usedDesignVersionId;
  const gensetComponentId = savedGenset?.componentId;
  const gensetRequestKey = [
    gensetDesignId,
    gensetDesignVersionId,
    gensetComponentId,
  ].join(":");
  const gensetTypeRequestRef = useRef(null);
  const [fetchedGensetType, setFetchedGensetType] = useState(null);

  const requestGensetType = useCallback(
    (requestKey) => {
      if (gensetTypeRequestRef.current?.key === requestKey) {
        return gensetTypeRequestRef.current.promise;
      }

      const request = { key: requestKey, promise: null };
      const promise = MGA_API.getComponentInfo(
        gensetDesignId,
        gensetDesignVersionId,
        gensetComponentId
      )
        .then((gensetDetails) => {
          const gensetType =
            gensetDetails?.componentAttributes?.gensetParameters?.GensetType ||
            "ControlledGenset";
          if (gensetTypeRequestRef.current === request) {
            setFetchedGensetType({ key: requestKey, type: gensetType });
          }
          return gensetType;
        })
        .catch((error) => {
          displayError(
            "error",
            getErrorMessage(error),
            "Failed to fetch genset operating mode"
          );
          if (gensetTypeRequestRef.current === request) {
            gensetTypeRequestRef.current = null;
          }
          return null;
        });

      request.promise = promise;
      gensetTypeRequestRef.current = request;
      return promise;
    },
    [gensetComponentId, gensetDesignId, gensetDesignVersionId]
  );

  useEffect(() => {
    if (
      canViewTabs?.hasThermalLoad &&
      !isVersionReadOnly &&
      gensetDesignId &&
      gensetDesignVersionId &&
      gensetComponentId
    ) {
      requestGensetType(gensetRequestKey);
    }
  }, [
    canViewTabs?.hasThermalLoad,
    gensetComponentId,
    gensetDesignId,
    gensetDesignVersionId,
    gensetRequestKey,
    isVersionReadOnly,
    requestGensetType,
  ]);

  const previousGensetEditingRef = useRef(
    Boolean(componentStatus?.genset?.isEditing)
  );

  useEffect(() => {
    const isGensetEditing = Boolean(componentStatus?.genset?.isEditing);
    const wasGensetEditing = previousGensetEditingRef.current;
    previousGensetEditingRef.current = isGensetEditing;

    if (
      wasGensetEditing &&
      !isGensetEditing &&
      componentStatus?.genset?.completed &&
      canViewTabs?.hasThermalLoad &&
      !isVersionReadOnly &&
      gensetDesignId &&
      gensetDesignVersionId &&
      gensetComponentId
    ) {
      gensetTypeRequestRef.current = null;
      setFetchedGensetType(null);
      requestGensetType(gensetRequestKey);
    }
  }, [
    canViewTabs?.hasThermalLoad,
    componentStatus?.genset?.completed,
    componentStatus?.genset?.isEditing,
    gensetComponentId,
    gensetDesignId,
    gensetDesignVersionId,
    gensetRequestKey,
    isVersionReadOnly,
    requestGensetType,
  ]);

  // Access modal state for blocked tab navigation
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [accessModalMessage, setAccessModalMessage] = useState("");
  const [, setNavigateToComponent] = useAtom(navigateToComponentAtom);
  // Thermal/genset Validation 1 modal (thermal selected but no genset configured at all)
  const [thermalNoGensetModal, setThermalNoGensetModal] = useState(false);
  // Thermal/genset Validation 3 modal (thermal selected but ControlledGenset configured)
  const [thermalControlledModal, setThermalControlledModal] = useState(false);
  // Pending navigation target when user clicks a tab and we need to confirm first
  const [pendingTab, setPendingTab] = useState(null);
  // Unsaved-edit blocked tab switch (OK/Cancel/Save flow)
  const [unsavedEditModalOpen, setUnsavedEditModalOpen] = useState(false);
  const [unsavedEditComponent, setUnsavedEditComponent] = useState("");
  const [pendingUnsavedTab, setPendingUnsavedTab] = useState(null);
  const [savingUnsavedComponent, setSavingUnsavedComponent] = useState(false);

  const navigateToTab = (tab) => {
    setActiveState(tab);
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab.toLowerCase());
    if (tab !== "DESIGN") {
      next.delete("component");
    }
    setSearchParams(next, { replace: true });
  };

  const handleSaveAndStayOnTab = async () => {
    if (isVersionReadOnly) {
      displayError("error", t("progressBar.editingRestricted"));
      setUnsavedEditModalOpen(false);
      setUnsavedEditComponent("");
      setPendingUnsavedTab(null);
      return;
    }

    setSavingUnsavedComponent(true);
    try {
      if (typeof saveTrigger === "function") {
        await saveTrigger();
      }
    } catch (e) {
      displayError('error', getErrorMessage(e), "Failed to save changes");
    } finally {
      setSavingUnsavedComponent(false);
      setUnsavedEditModalOpen(false);
      setUnsavedEditComponent("");
      setPendingUnsavedTab(null);
    }
  };

  return (
    <se-tabbar
      color="alternative"
      style={{
        zIndex: "10",
        boxShadow: "rgba(90, 116, 148, 0.3) 0px 4px 4px",
        marginBottom: activeState === "DESIGN" ? "4px" : "0",
      }}
    >
      {/* {fallbackModal} */}
      <nav className="row mx-0" style={{ zIndex: "10" }}>
        {["DESIGN", "SIMULATION", "RESULT"].map((activeTab) => (
          <a
            className={`col-sm-4 ${activeState === activeTab ? "active" : ""}`}
            role="button"
            tabIndex={0}
            onClick={async (e) => {
              if (!isVersionReadOnly && activeState === "DESIGN" && activeState !== activeTab) {
                const editingComponent = Object.entries(componentStatus || {}).find(
                  ([, status]) =>
                    status &&
                    typeof status === "object" &&
                    status?.isEditing &&
                    !status?.completed
                );
                if (editingComponent) {
                  const componentKey = editingComponent?.[0] || t("common.selected");
                  setUnsavedEditComponent(getComponentDisplayName(componentKey));
                  setPendingUnsavedTab(activeTab);
                  setUnsavedEditModalOpen(true);
                  return;
                }
              }

              // Simulation access check: allow click but block navigation and show error when prerequisites missing
              if (activeTab === "SIMULATION" && !canViewTabs?.canViewSimulation) {
                setAccessModalMessage('Design should have Controls to proceed Simulation');
                setAccessModalOpen(true);
                return;
              }
              // Validation 1: Thermal selected but no genset component configured at all
              // Validation 3: Thermal selected but ControlledGenset configured
              if (activeTab === "SIMULATION") {
                const hasThermalLoad = canViewTabs?.hasThermalLoad;
                if (hasThermalLoad) {
                  if (!savedGenset) {
                    // V1: no genset configured at all
                    setPendingTab(activeTab);
                    setThermalNoGensetModal(true);
                    return;
                  }
                  if (!isVersionReadOnly) {
                    if (
                      !gensetDesignId ||
                      !gensetDesignVersionId ||
                      !gensetComponentId
                    ) {
                      displayError(
                        "error",
                        "Unable to identify the selected genset component."
                      );
                      return;
                    }

                    const gensetType =
                      fetchedGensetType?.key === gensetRequestKey
                        ? fetchedGensetType.type
                        : await requestGensetType(gensetRequestKey);
                    if (!gensetType) {
                      return;
                    }

                    if (gensetType === "ControlledGenset") {
                      setPendingTab(activeTab);
                      setThermalControlledModal(true);
                      return;
                    }
                  }
                }
              }
              if (activeTab === "RESULT" && canViewTabs?.versionStatus !== "COMPLETED") {
                setAccessModalMessage('Please complete the simulation (Design Version Status must be set to COMPLETED) to access the Results.');
                setAccessModalOpen(true);
                return;
              }
              setActiveState(activeTab);
              const next = new URLSearchParams(searchParams);
              next.set("tab", activeTab.toLowerCase());
              // "component" tab selection only applies to the Design tab; strip it
              // when navigating away so it doesn't leak into Simulation/Result URLs.
              // Goes through react-router's setSearchParams (not a raw
              // window.history call) so useSearchParams/useLocation elsewhere
              // (LoadProject, the assessment page) actually see the change.
              if (activeTab !== "DESIGN") {
                next.delete("component");
              }
              setSearchParams(next, { replace: true });
            }}            key={activeTab}
            style={{
              justifyContent: "center",
              zIndex: "10",
              cursor: (activeTab === "SIMULATION" && !canViewTabs?.canViewSimulation) || (activeTab === "RESULT" && canViewTabs?.versionStatus !== "COMPLETED") ? 'not-allowed' : 'pointer',
              opacity: (activeTab === "RESULT" && canViewTabs?.versionStatus !== "COMPLETED") ? 0.55 : 1,
              borderBottom:
                activeState !== activeTab
                  ? "2px solid rgba(0, 0, 0, 0.1)"
                  : "none",
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.currentTarget.click();
              }
            }}
          >
            <span>{activeTab}</span>
          </a>
        ))}
      </nav>
      <nav slot="none"></nav>
      <SEConfirmModal
        opened={accessModalOpen}
        onClose={() => { setAccessModalOpen(false); setAccessModalMessage(""); }}
        onConfirm={() => { setAccessModalOpen(false); setAccessModalMessage(""); }}
        confirmLabel={t("common.ok")}
        showCancel={false}
        header={"Warning"}
        message={<div style={{ lineHeight: 1.4 }}>{accessModalMessage}</div>}
      />
      {/* Unsaved edit blocked tab switch: OK (stay) / Cancel (ignore & move) / Save (save & stay) */}
      <SEConfirmModal
        opened={unsavedEditModalOpen}
        onClose={() => {
          setUnsavedEditModalOpen(false);
          setUnsavedEditComponent("");
          if (pendingUnsavedTab) {
            navigateToTab(pendingUnsavedTab);
          }
          setPendingUnsavedTab(null);
        }}
        onConfirm={() => {
          setUnsavedEditModalOpen(false);
          setUnsavedEditComponent("");
          setPendingUnsavedTab(null);
        }}
        header={"Warning"}
        confirmSeColor="standard"
        message={
          <Stack spacing={4}>
            <Text>
              <Trans
                i18nKey="manageProgress.unsavedEdit.message"
                values={{ component: unsavedEditComponent }}
                components={{ b: <strong /> }}
              />
            </Text>
            <Text>{t("manageProgress.unsavedEdit.okHint")}</Text>
            <Text>{t("manageProgress.unsavedEdit.cancelHint")}</Text>
            <Text>{t("manageProgress.unsavedEdit.saveHint")}</Text>
          </Stack>
        }
        confirmLabel={t("common.ok")}
        cancelLabel={t("common.cancel")}
        confirmDisabled={savingUnsavedComponent}
        cancelDisabled={savingUnsavedComponent}
        onExtra={handleSaveAndStayOnTab}
        extraLabel={t("common.save")}
        extraLoading={savingUnsavedComponent}
      />
      {/* Validation 1: Thermal Load selected but no Genset configured */}
      <SEConfirmModal
        opened={thermalNoGensetModal}
        onClose={() => {
          // "Continue" → proceed to simulation tab without thermal in payload
          setThermalNoGensetModal(false);
          if (pendingTab) {
            setActiveState(pendingTab);
            const next = new URLSearchParams(searchParams);
            next.set("tab", pendingTab.toLowerCase());
            if (pendingTab !== "DESIGN") {
              next.delete("component");
            }
            setSearchParams(next, { replace: true });
            setPendingTab(null);
          }
        }}
        onConfirm={() => {
          // "Yes" → stay on DESIGN tab and navigate to Genset panel
          setThermalNoGensetModal(false);
          setPendingTab(null);
          setNavigateToComponent("genset");
        }}
        header={t("genset.thermalValidation.header")}
        message={
          <div>
            <p style={{ marginBottom: "0.75rem" }}>
              {t("genset.thermalValidation.noGensetWithThermal")}
            </p>
            <div style={{ border: "1px solid white", padding: "0.6rem 0.75rem", borderRadius: "4px" }}>
              <strong>{t("common.note")}</strong>
              <ul style={{ margin: "0.4rem 0 0 0", paddingLeft: "1.2rem" }}>
                <li><strong>{t("common.continue")}</strong> → {t("genset.thermalValidation.note.noGenset.continue")}</li>
                <li><strong>{t("common.yes")}</strong> → {t("genset.thermalValidation.note.noGenset.yes")}</li>
              </ul>
            </div>
          </div>
        }
        confirmLabel={t("common.yes")}
        cancelLabel={t("common.continue")}
        showCancel
      />
      {/* Validation 3: Thermal Load selected but ControlledGenset configured */}
      <SEConfirmModal
        opened={thermalControlledModal}
        onClose={() => {
          // "Continue" → proceed to simulation tab (thermal excluded from payload)
          setThermalControlledModal(false);
          if (pendingTab) {
            setActiveState(pendingTab);
            const next = new URLSearchParams(searchParams);
            next.set("tab", pendingTab.toLowerCase());
            if (pendingTab !== "DESIGN") {
              next.delete("component");
            }
            setSearchParams(next, { replace: true });
            setPendingTab(null);
          }
        }}
        onConfirm={() => {
          // "Yes" → stay on DESIGN tab and go to Genset panel
          setThermalControlledModal(false);
          setPendingTab(null);
          setNavigateToComponent("genset");
        }}
        header={t("genset.thermalValidation.header")}
        message={
          <div>
            <p style={{ marginBottom: "0.75rem" }}>
              {t("genset.thermalValidation.controlledWithThermal")}
            </p>
            <div style={{ border: "1px solid white", padding: "0.6rem 0.75rem", borderRadius: "4px" }}>
              <strong>{t("common.note")}</strong>
              <ul style={{ margin: "0.4rem 0 0 0", paddingLeft: "1.2rem" }}>
                <li><strong>{t("common.continue")}</strong> → {t("genset.thermalValidation.note.controlled.continue")}</li>
                <li><strong>{t("common.yes")}</strong> → {t("genset.thermalValidation.note.controlled.yes")}</li>
              </ul>
            </div>
          </div>
        }
        confirmLabel={t("common.yes")}
        cancelLabel={t("common.continue")}
        showCancel
      />
    </se-tabbar>
  );
};
export default ManageProgressBar;
